/**
 * WorkOrderEditModal.jsx
 * 
 * Inline modal for editing work order details without leaving the Order page.
 * Optimized for minimal clicks and fast workflow.
 * 
 * Features:
 * - Edit instructions/notes
 * - Update status
 * - Set priority, department, due date
 * - Add tasks/checklist items
 * - Auto-saves and closes, keeping user on Order page
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Factory,
  Loader2,
  Save,
  X,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
  Clock,
  FileText,
  ListChecks
} from 'lucide-react';
import { operationsAPI } from '../../utils/api';

// Status options (matching backend WorkOrderStatus enum)
const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: 'bg-blue-100 text-blue-800' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'done', label: 'Done', color: 'bg-green-100 text-green-800' }
];

// Department options
const DEPARTMENT_OPTIONS = [
  { value: 'sublimation', label: 'Sublimation', icon: '🔥' },
  { value: 'art', label: 'Art Department', icon: '🎨' },
  { value: 'printing', label: 'Printing', icon: '🖨️' },
  { value: 'cutting', label: 'Cutting', icon: '✂️' },
  { value: 'finishing', label: 'Finishing', icon: '✨' },
  { value: 'engraving', label: 'Engraving', icon: '🔧' },
  { value: 'laser', label: 'Laser', icon: '⚡' },
  { value: 'shipping', label: 'Shipping', icon: '📦' }
];

// Priority options
const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'rush', label: 'Rush', color: 'bg-red-100 text-red-700' }
];

const WorkOrderEditModal = ({ 
  open, 
  onClose, 
  workOrder, 
  onSave,
  orderId 
}) => {
  // Form state - using 'checklist' to match backend format {label, is_done}
  const [formData, setFormData] = useState({
    instructions: '',
    status: 'open',
    department: '',
    priority: 'normal',
    due_at: '',
    checklist: []
  });
  
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [newTask, setNewTask] = useState('');
  
  // Initialize form when workOrder changes - map to checklist format
  useEffect(() => {
    if (workOrder) {
      // Convert tasks to checklist format if needed, or use existing checklist
      const checklist = workOrder.checklist || (workOrder.tasks?.map(t => ({
        label: t.description || t.label,
        is_done: t.completed || t.is_done || false
      })) || []);
      
      setFormData({
        instructions: workOrder.instructions || '',
        status: workOrder.status || 'open',
        department: workOrder.department || '',
        priority: workOrder.priority || 'normal',
        due_at: workOrder.due_at ? workOrder.due_at.split('T')[0] : '',
        checklist: checklist
      });
    }
  }, [workOrder]);
  
  // Handle field change
  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };
  
  // Add checklist item
  const handleAddTask = () => {
    if (!newTask.trim()) return;
    
    const item = {
      label: newTask.trim(),
      is_done: false
    };
    
    setFormData(prev => ({
      ...prev,
      checklist: [...prev.checklist, item]
    }));
    setNewTask('');
  };
  
  // Remove checklist item
  const removeTask = (index) => {
    setFormData(prev => ({
      ...prev,
      checklist: prev.checklist.filter((_, i) => i !== index)
    }));
  };
  
  // Save work order
  const handleSave = async () => {
    if (!workOrder?.id) {
      setError('Work order ID is missing');
      return;
    }
    
    setSaving(true);
    setError(null);
    
    try {
      const updateData = {
        instructions: formData.instructions,
        priority: formData.priority,
        checklist: formData.checklist
      };
      
      // Only include department if set
      if (formData.department) {
        updateData.department = formData.department;
      }
      
      // Only include due_at if set
      if (formData.due_at) {
        updateData.due_at = new Date(formData.due_at).toISOString();
      }
      
      // Handle status change separately if changed
      if (formData.status !== workOrder.status) {
        await operationsAPI.updateWorkOrderStatus(workOrder.id, formData.status);
      }
      
      // Update other fields
      await operationsAPI.updateWorkOrder(workOrder.id, updateData);
      
      // Call onSave callback to refresh parent
      if (onSave) {
        onSave();
      }
      
      // Close modal
      onClose();
      
    } catch (err) {
      console.error('Failed to save work order:', err);
      setError(err.message || 'Failed to save work order');
    } finally {
      setSaving(false);
    }
  };
  
  // Get status color
  const getStatusColor = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status)?.color || 'bg-gray-100 text-gray-800';
  };
  
  if (!workOrder) return null;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="work-order-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Factory className="w-5 h-5 text-blue-600" />
            Edit Work Order
            <Badge variant="outline" className="ml-2 font-mono">
              {workOrder.work_order_number}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Update work order details. Changes save instantly.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4 space-y-6">
          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          
          {/* Department & Due Date Row */}
          <div className="grid grid-cols-2 gap-4">
            {/* Department */}
            <div className="space-y-2">
              <Label htmlFor="wo-department">Department</Label>
              <Select 
                value={formData.department} 
                onValueChange={(v) => handleChange('department', v)}
              >
                <SelectTrigger id="wo-department" data-testid="wo-department-select">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {DEPARTMENT_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>
                      <span>{opt.icon} {opt.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Due Date */}
            <div className="space-y-2">
              <Label htmlFor="wo-due-date">Due Date</Label>
              <Input
                id="wo-due-date"
                type="date"
                value={formData.due_at}
                onChange={(e) => handleChange('due_at', e.target.value)}
                data-testid="wo-due-date-input"
              />
            </div>
          </div>
          
          {/* Instructions / Notes */}
          <div className="space-y-2">
            <Label htmlFor="wo-instructions" className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              Instructions / Notes
            </Label>
            <Textarea
              id="wo-instructions"
              value={formData.instructions}
              onChange={(e) => handleChange('instructions', e.target.value)}
              placeholder="Add production instructions, special requirements, or notes..."
              rows={4}
              className="resize-none"
              data-testid="wo-instructions-textarea"
            />
            <p className="text-xs text-gray-500">
              These instructions will be visible to production staff
            </p>
          </div>
          
          {/* Tasks List (Numbered) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-gray-500" />
              Tasks
            </Label>
            
            {/* Add Task Input */}
            <div className="flex gap-2">
              <Input
                value={newTask}
                onChange={(e) => setNewTask(e.target.value)}
                placeholder="Add a task..."
                onKeyDown={(e) => e.key === 'Enter' && handleAddTask()}
                data-testid="wo-new-task-input"
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleAddTask}
                disabled={!newTask.trim()}
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Task List - Simple Numbered */}
            {formData.checklist.length > 0 ? (
              <div className="border rounded-lg divide-y max-h-48 overflow-y-auto">
                {formData.checklist.map((item, index) => (
                  <div 
                    key={index} 
                    className="flex items-center gap-3 p-3 bg-white hover:bg-gray-50"
                  >
                    <span className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full text-xs font-medium text-gray-600">
                      {index + 1}
                    </span>
                    <span className="flex-1 text-sm">
                      {item.label}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeTask(index)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400 italic py-2">No tasks added yet</p>
            )}
            
            {/* Task Count */}
            {formData.checklist.length > 0 && (
              <p className="text-xs text-gray-500">
                {formData.checklist.length} task{formData.checklist.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
        
        <DialogFooter className="flex justify-between items-center border-t pt-4">
          <div className="text-xs text-gray-500">
            Last updated: {workOrder.updated_at ? new Date(workOrder.updated_at).toLocaleString() : 'Never'}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} disabled={saving}>
              <X className="w-4 h-4 mr-1" />
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="wo-save-btn">
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkOrderEditModal;
